/** Small DOM helpers. No framework: the app is a handful of views over live numbers. */

export function escape(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[c]!,
  );
}

/** Tagged template that escapes every interpolation. Use `raw()` to opt out. */
export function html(strings: TemplateStringsArray, ...values: unknown[]): string {
  let out = strings[0] ?? '';
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    out += (v instanceof Raw ? v.value : Array.isArray(v) ? v.map(flatten).join('') : escape(v)) + (strings[i + 1] ?? '');
  }
  return out;
}

class Raw {
  constructor(readonly value: string) {}
}

/** Marks a string as already-safe HTML. */
export function raw(value: string): Raw {
  return new Raw(value);
}

function flatten(v: unknown): string {
  return v instanceof Raw ? v.value : escape(v);
}

export const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T | null =>
  root.querySelector<T>(sel);

export const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] =>
  [...root.querySelectorAll<T>(sel)];

/** Replaces an element's content only when it changed, so the DOM is not churned every second. */
export function setHtml(el: Element | null, next: string): void {
  if (el && el.innerHTML !== next) el.innerHTML = next;
}

export function setText(el: Element | null, next: string): void {
  if (el && el.textContent !== next) el.textContent = next;
}

/** Delegated click handling keyed on `data-action`. */
export function onAction(root: Element, handlers: Record<string, (el: HTMLElement, ev: Event) => void>): void {
  root.addEventListener('click', (ev) => {
    const target = (ev.target as Element | null)?.closest<HTMLElement>('[data-action]');
    if (!target) return;
    const handler = handlers[target.dataset.action ?? ''];
    if (handler) {
      ev.preventDefault();
      handler(target, ev);
    }
  });
}
